/** CSS contract: css-library/components/01-leaves/button.css. */
import type { Ref } from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';
import { ButtonControl, Tooltip } from './ButtonControl';
import { cx } from '../lib/cx';

export type ButtonVariant = 'default' | 'primary' | 'danger' | 'success' | 'text' | 'option' | 'filter-reset' | 'dictation';

export type ButtonSize = 'default' | 'lg';

const buttonClasses: Record<ButtonVariant, string> = {
  default: 'btn', primary: 'btn btn--primary', danger: 'btn btn--danger', success: 'btn btn--success',
  text: 'btn-text', option: 'select-option',
  'filter-reset': 'filter-menu-reset', dictation: 'dictation-button',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  primaryAction?: boolean;
  variant?: ButtonVariant;
  isActive?: boolean;
  iconOnly?: boolean;
  size?: ButtonSize;
  block?: boolean;
}

export function Button(
  { ref, variant = 'default',
    primaryAction = false,
    isActive = false,
    iconOnly = false,
    size = 'default',
    block = false,
    loading = false,
    className,
    type = 'button',
    children,
    ...rest
  }: ButtonProps & { ref?: Ref<HTMLButtonElement> },
) {
  return (
    <ButtonControl
      ref={ref}
      type={type}
      className={cx(
        buttonClasses[primaryAction ? 'success' : variant],
        iconOnly && !primaryAction && 'btn-icon',
        size !== 'default' && `btn--${size}`,
        block && 'btn--block',
        isActive && 'is-active',
        loading && 'is-loading',
        className,
      )}
      {...rest}
      disabled={loading || rest.disabled}
      aria-busy={loading || rest['aria-busy']}
      aria-label={rest['aria-label'] ?? (iconOnly && !rest['aria-labelledby'] ? rest.title : undefined)}
    >
      {children}
      {primaryAction && iconOnly && (rest.title ?? rest['aria-label'])}
    </ButtonControl>
  );
}

export interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: 'default' | 'primary';
  block?: boolean;
}

export function ButtonLink(
  { ref, variant = 'default', block = false, className, ...props }: ButtonLinkProps & { ref?: Ref<HTMLAnchorElement> },
) {
  return <Tooltip><a ref={ref} className={cx(buttonClasses[variant], block && 'btn--block', className)} {...props} /></Tooltip>;
}
